import * as vp from 'vopay';
import moment from 'moment';
import { FunctionComponent, useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom'; // Import ReactDOM for creating the portal
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useLayout } from '../../../../providers/Layout.provider';
import { useToken } from '../../../../providers/Token.provider';
import { usePaylink } from '../../../../providers/Paylink.provider';
import styles from './PartialPayment.module.scss';
import PaymentMethods from '../PaymentMethods/PaymentMethods';
import PartialPaymentForm from './PartialPaymentForm';
import { useFlexiblePayment } from '../../../../providers/FlexiblePayment.provider';
import { PartialPaymentValues } from './types';

const { Box, Title, Tooltip, Modal } = vp;

type FC = FunctionComponent;

const PartialPayment: FC = () => {
  const { setLayoutInfo } = useLayout();
  const { token, isSetByBrowser } = useToken();
  const { paylink } = usePaylink();
  const { updateFlexiblePayment, updatePartialPaymentValues } = useFlexiblePayment();
  const history = useHistory();
  const [connectionMethod, setConnectionMethod] = useState<vp.ConnectionTypes | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [remainingAmount, setRemainingAmount] = useState<PartialPaymentValues['Amount']>(paylink.Amount);

  const { t } = useTranslation('translations', { keyPrefix: 'PartialPayment' });

  useEffect(() => {
    setLayoutInfo(
      vp.chooseColor<string | null | undefined>('#FFD147', token?.BrandingDetails?.Branding?.PrimaryColor),
      'curve',
      '',
      70,
      32,
      0,
      !isSetByBrowser,
      true,
    );
  }, []);

  const defaultValues = {
    Amount: null,
    RemainderDate: '',
  };

  const onPartialPayment = useCallback(
    (values: PartialPaymentValues) => {
      updateFlexiblePayment(true, vp.FlexiblePaymentMethods.PartialPayment);
      updatePartialPaymentValues({
        Amount: String(values.Amount),
        RemainderPaymentDate: values.RemainderDate ? moment(Number(values.RemainderDate)).format('YYYY-MM-DD') : '',
      });

      if (connectionMethod === 'bank') {
        history.push('/connect/processor/bank');
      }
      if (connectionMethod === 'credit') {
        history.push({
          pathname: '/connect/processor/credit',
          state: { origin: 'token' },
        });
      }
      if (connectionMethod === 'debitcard') {
        history.push('/connect/processor/debitcard');
      }
      if (connectionMethod === 'email') {
        history.push({
          pathname: '/connect/processor/email',
          state: { origin: 'token' },
        });
      }
    },
    [connectionMethod],
  );

  const handleRemainingAmountChange = useCallback((amount: PartialPaymentValues['Amount']) => {
    if (!amount) {
      setRemainingAmount(paylink.Amount);
    } else {
      const remaining = Number(paylink.Amount) - Number(amount);
      setRemainingAmount(remaining.toFixed(2));
    }
  }, []);

  return (
    <Box jc="center">
      {ReactDOM.createPortal(
        <Modal isVisible={isModalOpen} enableBgClick={false}>
          <PartialPaymentForm
            defaultValues={defaultValues}
            onPartialPayment={onPartialPayment}
            isModalOpen={isModalOpen}
            remainingAmount={remainingAmount}
            onClose={setIsModalOpen}
            onAmountChange={handleRemainingAmountChange}
          />
        </Modal>,
        document.getElementById('modal-root')!, // Renders the modal at the root of the document
      )}
      <Box flx="col" jc="center" ai="center" base="8" gap="md" maxwidth="545px">
        <Box pt="lg" base="8" gap="sm" ai="center" height="auto">
          <Title title={`${t('title')}`} size="lg" bold />
          <Tooltip
            information={`${t('partialPaymentDescription')}`}
            icon="question"
            position="left"
            variant="outlined"
            width="235px"
            fontSize="16px"
            positionX="-100%"
            positionY="-46%"
            textAlign="left"
          />
        </Box>
        <Box base="8" gap="common" flx="col" height="auto">
          <Box jc="center">
            <Box gapSize="10px" base="8" flx="col" boundary="shadow" p="md" r="md" maxwidth="280px" width="280px">
              <div className={styles.totalTitle}>{`${t('total')}`}</div>
              <div className={styles.totalAmount}>
                {paylink.Currency} {vp.formattedCurrency(paylink.Amount)}
              </div>
              <div className={styles.totalRemaining}>
                {`${t('remaining')} ${paylink.Currency} ${
                  Number(vp.sanitizeToNumeric(remainingAmount || '')) >= 0
                    ? vp.formattedCurrency(remainingAmount || '')
                    : vp.formattedCurrency(paylink.Amount || '')
                }`}
              </div>
            </Box>
          </Box>
          <Box>
            <div className={styles.selectionDesc}>{`${t('selection')}`}</div>
          </Box>
          <PaymentMethods
            flexiblePaymentMethod={vp.FlexiblePaymentMethods.PartialPayment}
            onClick={(connectionType: vp.ConnectionTypes) => {
              setConnectionMethod(connectionType);
              setIsModalOpen(!isModalOpen);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PartialPayment;
